import React, { useEffect, useRef, useState } from 'react'
import {
  Avatar,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  TextField,
  Typography,
  useTheme,
} from '@mui/material'
// import { makeSyles } from '@mui/styles'
import Webcam from 'react-webcam'
import useCustomStyles from '../../utils/hooks/CustomStylesHook'
import { toast } from 'react-toastify'
import CameraAltIcon from '@mui/icons-material/CameraAlt'
import DriveFolderUploadIcon from '@mui/icons-material/DriveFolderUpload'
import ClearIcon from '@mui/icons-material/Clear'
import { uploadImage } from '../../utils/api'
import { FILE_SERVER_URL } from '../../utils/constants'
import {
  captureImage,
  PROFILE_PICTURE_ADD_PHOTO,
  PROFILE_PICTURE_CAMERA_PERMISSION_DENIED,
  PROFILE_PICTURE_CANCEL,
  PROFILE_PICTURE_CAPTURE_UPLOAD_FAILED,
  PROFILE_PICTURE_SELECT_CAMERA,
  PROFILE_PICTURE_UPDATE_TITLE,
  PROFILE_PICTURE_UPLOAD_FAILED,
  PROFILE_PICTURE_UPLOAD_FILE_NAME,
  PROFILE_PICTURE_UPLOAD_FROM_DEVICE,
  PROFILE_PICTURE_UPLOAD_RESPONSE_MISSING_FILE_KEY,
} from './constants'

const styles = (theme: any) => ({
  avatar: {
    width: 150,
    height: 120,
    marginBottom: theme?.spacing(2),
    cursor: 'pointer',
    position: 'relative',
    borderRadius: 8,
  },
  input: {
    display: 'none',
  },
  dialogContent: {
    textAlign: 'center',
  },
  webcam: {
    width: '100%',
    borderRadius: theme?.shape?.borderRadius,
  },
  buttonStyles: {
    width: 'fit-content',
    textTransform: 'capitalize',
  },
  overlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: 150,
    height: 120,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    color: theme.palette.text.secondary,
  },
})

const videoConstraints = {
  width: 1280,
  height: 720,
  facingMode: 'user',
}

type Props = {
  photo?: string
  onPhotoChange?: (photo: string) => void
}

const ProfilePictureOne: React.FC<Props> = ({ photo = '', onPhotoChange }) => {
  const theme = useTheme()
  const classes = useCustomStyles(styles, theme)
  const [image, setImage] = useState<string | null>(photo || null)
  const [open, setOpen] = useState(false)

  const [devices, setDevices] = useState<MediaDeviceInfo[]>([])
  const [selectedDeviceId, setSelectedDeviceId] = useState<string | null>(null)
  const webcamRef = useRef<Webcam>(null)

  const uploadPhotoFile = async (file: Blob) => {
    const formData = new FormData()
    formData.append('file', file, PROFILE_PICTURE_UPLOAD_FILE_NAME)
    const data: any = await uploadImage(formData)
    const uploaded = data?.data?.[0]
    if (!uploaded) {
      throw new Error(PROFILE_PICTURE_UPLOAD_RESPONSE_MISSING_FILE_KEY)
    }

    const uploadedUrl = `${FILE_SERVER_URL}file/${uploaded}`
    setImage(uploadedUrl)
    onPhotoChange?.(uploadedUrl)
    setOpen(false)
  }

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e?.target?.files?.[0]
    if (file) {
      try {
        await uploadPhotoFile(file)
      } catch (error) {
        toast.error(PROFILE_PICTURE_UPLOAD_FAILED)
      }
    }
  }

  async function initDevices() {
    try {
      // Request camera access
      const stream = await navigator?.mediaDevices?.getUserMedia({
        video: true,
      })

      // Enumerate devices after permission
      const mediaDevices = await navigator?.mediaDevices?.enumerateDevices()
      const videoDevices = mediaDevices?.filter(
        (device) => device?.kind === 'videoinput'
      )
      setDevices(videoDevices)

      if (videoDevices?.length > 0) {
        setSelectedDeviceId(videoDevices?.[0].deviceId)
      }

      // Stop the camera stream immediately after use
      stream?.getTracks()?.forEach((track) => track?.stop())
    } catch (error) {
      toast.error(PROFILE_PICTURE_CAMERA_PERMISSION_DENIED)
    }
  }

  const handleCapture = async () => {
    const screenshot = webcamRef?.current?.getScreenshot()
    if (screenshot) {
      try {
        const response = await fetch(screenshot)
        const imageBlob = await response.blob()
        await uploadPhotoFile(imageBlob)
      } catch (error) {
        toast.error(PROFILE_PICTURE_CAPTURE_UPLOAD_FAILED)
      }
    }
  }

  const handleClearPhoto = () => {
    setImage(null)
    onPhotoChange?.('')
  }

  useEffect(() => {
    setImage(photo || null)
  }, [photo])

  useEffect(() => {
    initDevices()
  }, [])

  return (
    <Box
      display={'flex'}
      flexDirection='column'
      alignItems='center'
      boxShadow={5}
      borderRadius={3}
      p={0.5}
      width={'fit-content'}
      data-testid='ProfilePictureOne'
    >
      <Box position='relative' onClick={() => setOpen(true)}>
        {/* <Box
          component={'img'}
          src={image || ''}
          // alt='profile-picture-registration'
          width={200}
          height={140}
          borderRadius={2}
        /> */}

        <Avatar src={image || ''} alt='' className={classes.avatar}>
          {image ? null : <Box component={'span'} display='none' />}
        </Avatar>

        {!image && (
          <Box className={classes?.overlay}>
            <CameraAltIcon fontSize='large' />
            <Typography variant='caption'>
              {PROFILE_PICTURE_ADD_PHOTO}
            </Typography>
          </Box>
        )}
      </Box>
      <Box
        display={'flex'}
        justifyContent={'center'}
        flexDirection={'row'}
        gap={2}
      >
        <IconButton onClick={() => setOpen(true)} size='small'>
          <CameraAltIcon htmlColor='#4682b4' fontSize='small' />
        </IconButton>
        <IconButton onClick={() => setOpen(true)} size='small'>
          <DriveFolderUploadIcon htmlColor='#4682b4' fontSize='small' />
        </IconButton>
        <IconButton onClick={handleClearPhoto} size='small'>
          <ClearIcon htmlColor='#4682b4' fontSize='small' />
        </IconButton>
      </Box>

      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        maxWidth='xs'
        fullWidth
      >
        <DialogTitle>{PROFILE_PICTURE_UPDATE_TITLE}</DialogTitle>
        <DialogContent className={classes?.dialogContent} dividers>
          <Box mt={1} mb={2}>
            <TextField
              select
              fullWidth
              variant='outlined'
              label={PROFILE_PICTURE_SELECT_CAMERA}
              value={selectedDeviceId}
              onChange={(e) => setSelectedDeviceId(e?.target?.value)}
              size='small'
              data-testid='selectCam'
              InputLabelProps={{ shrink: true }}
            >
              {devices?.map((device) => (
                <MenuItem key={device?.deviceId} value={device?.deviceId}>
                  {device?.label}
                </MenuItem>
              ))}
            </TextField>
          </Box>
          {selectedDeviceId && (
            <Webcam
              height={250}
              width={300}
              ref={webcamRef}
              className={classes?.webcam}
              audio={false}
              screenshotFormat='image/jpeg'
              videoConstraints={{
                deviceId: { exact: selectedDeviceId },
                ...videoConstraints,
              }}
            />
          )}

          <input
            accept='image/*'
            className={classes?.input}
            id='upload-button-file'
            type='file'
            onChange={handleFileChange}
          />
          <label htmlFor='upload-button-file'>
            <Button
              variant='outlined'
              component='span'
              sx={{ mt: 2 }}
              size='small'
              className={classes?.buttonStyles}
              startIcon={<DriveFolderUploadIcon />}
            >
              {PROFILE_PICTURE_UPLOAD_FROM_DEVICE}
            </Button>
          </label>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setOpen(false)}
            size='small'
            className={classes?.buttonStyles}
          >
            {PROFILE_PICTURE_CANCEL}
          </Button>
          <Button
            onClick={handleCapture}
            variant='contained'
            color='primary'
            size='small'
            className={classes?.buttonStyles}
          >
            {captureImage}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default ProfilePictureOne
